// Phase 1B -- Briefing: an executive briefing, not a dashboard.
// One conclusion -> the evidence behind it -> one action. Everything comes
// from the read-only Decisions aggregation (real, counted evidence); when
// there is nothing to say, the page says so.
//
// Fitts:  the only filled button on the page is "Review decision", placed
//         directly under the conclusion it acts on.
// Hick:   one "what would help next" item, never a menu of set-up tasks.
// Miller: three numbers, not twelve; detail lives on the decision.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { connectorsAPI } from '@/lib/intelligenceApi';
import { useAuth } from '@/contexts/AuthContext';
import { hasIntelligenceAuditAccess } from '../intelligenceNav';
import { useFounderWorkspace, useFounderPath } from '../FounderWorkspace';
import {
  formatCheckFailureReason,
  formatMeasurementState,
  isCheckStale,
  latestCheckAttention,
  latestCheckBlocksAllClear,
  usablePromptCount,
} from '@/lib/somHistoryHelpers';
import { Eyebrow, PrimaryButton, SecondaryButton, TextButton, Stat, Section, EmptyState, Loading, AskButton, formatDate } from '../ui';

const GSC_INACTIVE = new Set(['NOT_CONNECTED', 'DISCONNECTED', 'CREDENTIAL_REVOKED', undefined, '']);

function gscIsLive(connectors) {
  const rec = (connectors || []).find((c) => c.provider === 'google_search_console');
  return Boolean(rec && !GSC_INACTIVE.has(rec.status));
}

function nextUnlock(summary, fp, gscLive) {
  // The single most valuable next step, from real state only.
  if (summary?.monitoring && !summary.monitoring.approved_watchlist) {
    return {
      title: 'Approve the questions Martian watches',
      body: 'Martian re-checks AI answers every week once you approve them — that is how it will measure whether the work is helping.',
      action: 'Review questions',
      to: fp('/business/watchlist'),
      testId: 'briefing-next-watchlist',
    };
  }
  // Unknown = connectors still loading. Do not flash a "connect GSC" CTA
  // over a workspace that already has Search Console connected.
  if (gscLive !== false) return null;
  return {
    title: 'Connect Google Search Console',
    body: 'Gives Martian your real search demand — the questions customers actually type.',
    action: 'See connections',
    to: fp('/evidence/connections'),
    testId: 'briefing-next-connect',
  };
}

export default function BriefingPage() {
  const { summary, displayName, workspaceId, loadingClients, openAsk } = useFounderWorkspace();
  const navigate = useNavigate();
  const fp = useFounderPath();
  const [gscLive, setGscLive] = useState(null);

  useEffect(() => {
    if (!workspaceId) {
      setGscLive(false);
      return undefined;
    }
    let cancelled = false;
    connectorsAPI.list(workspaceId)
      .then((res) => { if (!cancelled) setGscLive(gscIsLive(res.data?.connectors)); })
      .catch(() => { if (!cancelled) setGscLive(false); });
    return () => { cancelled = true; };
  }, [workspaceId]);

  if (loadingClients || (workspaceId && !summary)) return <Loading label="Preparing your briefing…" />;
  if (!workspaceId) {
    return (
      <EmptyState
        title="Tell Martian about your business"
        body="Add your website and Martian will read it, work out what you sell and who you compete with, and prepare your first briefing."
        action={<PrimaryButton to="/onboarding" testId="briefing-start-onboarding">Add your website</PrimaryButton>}
      />
    );
  }

  const decisions = summary?.decisions || [];
  const lead = decisions[0];
  const needsYou = summary?.needs_you || 0;
  const metrics = lead?.metrics || {};
  const work = lead?.prepared_work;
  const unlock = nextUnlock(summary, fp, gscLive);
  const brand = displayName || summary?.brand || 'your brand';
  const attention = latestCheckAttention(summary);
  const blocksAllClear = latestCheckBlocksAllClear(attention);

  if (!lead) {
    if (blocksAllClear) {
      return (
        <div data-testid="briefing-page">
          <Eyebrow className="mb-2">Briefing · {brand}</Eyebrow>
          <FailedCheckNotice attention={attention} fp={fp} />
          {unlock && <div className="mt-8"><NextUnlock unlock={unlock} /></div>}
          <ActivateWorkspaceNote />
        </div>
      );
    }
    return (
      <div data-testid="briefing-page">
        <Eyebrow className="mb-2">Briefing · {brand}</Eyebrow>
        <h1 className="font-display text-[28px] font-bold tracking-tight">Nothing needs you right now</h1>
        <p className="mt-2 text-[15px] text-[var(--text-secondary)] max-w-[60ch]">
          {summary?.checks ? 'Martian has checked AI answers for your business and has no decision for you yet.' : "Martian hasn't finished its first check of AI answers for your business yet. Your first briefing appears here when it has."}
        </p>
        {unlock && <div className="mt-8"><NextUnlock unlock={unlock} /></div>}
        <ActivateWorkspaceNote />
      </div>
    );
  }

  const eyebrow = blocksAllClear
    ? (attention.kind === 'incomplete' ? 'Latest check did not have enough answers' : 'Latest check could not be completed')
    : (needsYou ? `${needsYou} decision${needsYou === 1 ? '' : 's'} need${needsYou === 1 ? 's' : ''} you` : 'Nothing needs you right now');

  return (
    <div data-testid="briefing-page">
      {/* THIS WEEK -- the one conclusion and the one action */}
      <div className="flex items-center justify-between gap-4 mb-3">
        <Eyebrow tone={blocksAllClear || needsYou ? 'accent' : 'muted'} testId="briefing-needs-you">
          {eyebrow}
        </Eyebrow>
        <div className="hidden md:block"><AskButton onClick={() => openAsk(null)} /></div>
      </div>
      {blocksAllClear && <FailedCheckNotice attention={attention} fp={fp} compact />}
      <section className="rounded-[var(--radius-card-soft)] bg-[var(--surface)] border border-[var(--border)] shadow-[var(--shadow-card)] p-6 md:p-8" data-testid="briefing-lead">
        <h1 className="font-display text-[24px] md:text-[30px] font-bold tracking-tight leading-[1.2] text-balance max-w-[30ch]" data-testid="briefing-lead-found">
          {lead.found}
        </h1>
        {lead.why && <p className="mt-3 text-[15px] text-[var(--text-secondary)] leading-relaxed max-w-[62ch]">{lead.why}</p>}
        <div className="mt-6 flex flex-wrap items-center gap-4">
          <PrimaryButton size="lg" onClick={() => navigate(fp(`/decisions/${encodeURIComponent(lead.id)}`))} testId="briefing-review-decision">
            Review decision <ArrowRight size={16} weight="bold" />
          </PrimaryButton>
          <TextButton onClick={() => openAsk(lead.ask_context)} testId="briefing-ask-why">Ask Martian why</TextButton>
        </div>
      </section>

      {/* WHAT MARTIAN SAW -- three counted numbers */}
      {metrics.answers_checked > 0 && (
        <Section eyebrow={`What Martian saw · latest check ${formatDate(lead.observed_at)}`} testId="briefing-saw" className="mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 mt-2">
            <Stat value={metrics.brand_mentions} of={metrics.answers_checked} label={`AI answers mention ${brand}`} tone={metrics.brand_mentions === 0 ? 'bad' : undefined} testId="briefing-stat-brand" />
            {metrics.leader && <Stat value={metrics.leader.mentions} of={metrics.answers_checked} label={`mention ${metrics.leader.name}`} testId="briefing-stat-leader" />}
            {metrics.sources_total > 0 && <Stat value={metrics.sources_mention_brand} of={metrics.sources_total} label={`sources AI cited mention ${brand}`} tone={metrics.sources_mention_brand === 0 ? 'bad' : undefined} testId="briefing-stat-sources" />}
          </div>
        </Section>
      )}

      {/* WHAT MARTIAN THINKS */}
      {lead.where && (
        <Section eyebrow="What Martian thinks" testId="briefing-thinks">
          <p className="text-[16px] leading-relaxed max-w-[62ch]">{lead.where}</p>
          <div className="mt-3 flex gap-5">
            <TextButton onClick={() => openAsk(lead.ask_context)}>Why?</TextButton>
            <TextButton to={fp(`/decisions/${encodeURIComponent(lead.id)}#evidence`)}>See evidence</TextButton>
          </div>
        </Section>
      )}

      {/* WHAT MARTIAN RECOMMENDS */}
      {lead.recommendation && (
        <Section eyebrow="What Martian recommends" testId="briefing-recommends">
          <p className="font-display text-[19px] font-semibold leading-snug max-w-[50ch] text-balance">{lead.recommendation}</p>
          <div className="mt-3"><TextButton to={fp(`/decisions/${encodeURIComponent(lead.id)}`)}>Review recommendation</TextButton></div>
        </Section>
      )}

      {/* READY FOR YOU */}
      {work && (work.status === 'prepared' || work.status === 'reviewing') && (
        <Section eyebrow="Ready for you" testId="briefing-ready">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[12.5px] text-[var(--text-tertiary)]">Content brief · prepared {formatDate(work.created_at)}</p>
              <p className="mt-1 text-[16px] font-semibold leading-snug max-w-[60ch]">{work.headline || work.title}</p>
            </div>
            <SecondaryButton to={fp(`/work?item=${encodeURIComponent(work.id)}`)} testId="briefing-review-work">Review work</SecondaryButton>
          </div>
        </Section>
      )}

      {summary?.grounding_notice && (
        <Section eyebrow="A note from Martian" testId="briefing-grounding-notice">
          <p className="text-[14.5px] max-w-[62ch]">{summary.grounding_notice}</p>
          <div className="mt-2"><TextButton to={fp('/business')} tone="accent">Check your business details</TextButton></div>
        </Section>
      )}

      {/* WHAT WOULD HELP NEXT -- exactly one, and only when there is a real next step */}
      {unlock && (
        <Section eyebrow="What would help next" testId="briefing-next">
          <NextUnlock unlock={unlock} />
        </Section>
      )}

      {decisions.length > 1 && (
        <Section eyebrow="Also on Martian's radar" testId="briefing-more">
          <ul className="divide-y divide-[var(--border)]">
            {decisions.slice(1, 4).map((d) => (
              <li key={d.id}>
                <button type="button" onClick={() => navigate(fp(`/decisions/${encodeURIComponent(d.id)}`))}
                  className="w-full flex items-center justify-between gap-4 py-3 text-left hover:text-[var(--accent)]">
                  <span className="text-[14.5px]">{d.found}</span>
                  <span className="text-[12.5px] text-[var(--text-tertiary)] shrink-0">{d.channel}</span>
                </button>
              </li>
            ))}
          </ul>
          {decisions.length > 4 && <TextButton to={fp('/decisions')}>All decisions</TextButton>}
        </Section>
      )}

      <ActivateWorkspaceNote />
    </div>
  );
}

function ActivateWorkspaceNote() {
  const { user, activateFullWorkspace } = useAuth();
  const [activating, setActivating] = useState(false);
  if (user?.plan !== 'free') return null;

  const onActivate = async () => {
    setActivating(true);
    try {
      await activateFullWorkspace();
      toast.success('Full workspace unlocked');
    } catch {
      toast.error("Couldn't activate the workspace");
    } finally {
      setActivating(false);
    }
  };

  return (
    <Section eyebrow="Your workspace" testId="briefing-activate" className="mt-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[16px] font-semibold">There is more Martian can open</p>
          <p className="mt-1 text-[14px] text-[var(--text-secondary)] max-w-[60ch] leading-relaxed">
            {hasIntelligenceAuditAccess(user)
              ? 'Intelligence pages on this workspace are open for a controlled product audit. Activating the full workspace would change the subscription plan and also unlock Creator and growth-pipeline surfaces — that is a separate step.'
              : 'Activate your full workspace to unlock AI Discovery, Growth, and every connected surface.'}
          </p>
        </div>
        <SecondaryButton onClick={onActivate} disabled={activating} testId="briefing-activate-button">
          {activating ? 'Activating…' : 'Activate full workspace'}
        </SecondaryButton>
      </div>
    </Section>
  );
}

function FailedCheckNotice({ attention, fp, compact = false }) {
  const check = attention.check;
  const incomplete = attention.kind === 'incomplete';
  const tested = check?.prompts_tested;
  const usable = usablePromptCount(check);
  const coverage = (usable != null && tested != null)
    ? `${usable} of ${tested} questions got an answer.`
    : null;
  const heading = incomplete
    ? 'The latest AI check did not have enough answers'
    : 'The latest AI check could not be completed';
  const HeadingTag = compact ? 'h2' : 'h1';
  return (
    <section className={compact ? 'mb-4 rounded-[var(--radius-card-soft)] bg-[var(--surface)] border border-[var(--border)] p-5' : undefined} data-testid="briefing-failed-check">
      <HeadingTag className={`font-display font-bold tracking-tight ${compact ? 'text-[20px]' : 'text-[28px]'}`}>{heading}</HeadingTag>
      <p className="mt-2 text-[15px] text-[var(--text-secondary)] max-w-[60ch]">
        {check?.created_at ? `Checked ${formatDate(check.created_at, true)}. ` : ''}
        {formatMeasurementState(check)}. {formatCheckFailureReason(check)}
        {coverage ? ` ${coverage}` : ''}
        {isCheckStale(check) ? ' No newer check has completed since then.' : ''}
      </p>
      <p className="mt-2 text-[14px] text-[var(--text-secondary)] max-w-[60ch]">
        That is not the same as finding nothing that needs you. See the recorded checks, then decide whether to run another one.
      </p>
      <div className="mt-5">
        {compact
          ? <TextButton to={fp('/results')} testId="briefing-see-failed-check">See the recorded checks</TextButton>
          : <PrimaryButton to={fp('/results')} testId="briefing-see-failed-check">See the recorded checks</PrimaryButton>}
      </div>
    </section>
  );
}

function NextUnlock({ unlock }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid={unlock.testId}>
      <div className="min-w-0">
        <p className="text-[16px] font-semibold">{unlock.title}</p>
        <p className="mt-1 text-[14px] text-[var(--text-secondary)] max-w-[60ch] leading-relaxed">{unlock.body}</p>
      </div>
      <SecondaryButton to={unlock.to}>{unlock.action}</SecondaryButton>
    </div>
  );
}
